import { useEffect, useRef, useState } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useAuth } from '../hooks/useAuth.ts';
import { useUiStore } from '../stores/ui.ts';
import { NO_VALUE } from '../components/format.ts';
import {
  cognitoConfig,
  cognitoLogoutUrl,
  type CognitoConfig,
} from '../auth/config.ts';
import { Icon } from './Icon.tsx';
import styles from './UserBadge.module.css';

/* ==========================================================================
   UserBadge — who is signed in, and on whose authority.

   Not a pill and not a photograph. A 999px capsule wrapped around a round
   avatar is the single most recognisable piece of admin-template chrome
   there is, and an operations console has no use for a Gravatar: the operator
   knows what they look like. What is worth 130px of a 34px header is the
   account string, and what is worth the popover underneath it is the
   provenance of that account — which tenant, which role, which auth plane,
   which host is serving the API. Those four facts decide whether a command
   the operator is about to issue will be accepted.

   ---------------------------------------------------------------------------
   IT NEVER UNMOUNTS
   ---------------------------------------------------------------------------
   The last round returned null until the principal resolved, so a console
   whose API had died lost its identity control entirely — and with it the
   tenant, the role, the auth plane, the host and the ONLY theme switch in the
   build, at exactly the moment an operator is trying to work out whether the
   problem is the platform or their session. A control that disappears when
   things go wrong is a control that is missing when it is needed.

   So: three principal states, three renderings, one geometry.

     RESOLVED    the address, and every provenance row filled.
     ANONYMOUS   `/api/auth/me` answered and declared no principal. The word,
                 in --text-low. Not an error — an IAP deployment with the
                 header stripped is legitimately anonymous.
     UNRESOLVED  the read failed or has not landed. `—` in the trigger and in
                 every row it cannot fill, the endpoint and the error verbatim
                 in a fault line, and a Retry. The rows that do NOT depend on
                 the API — the auth plane, the host, the theme — stay real,
                 because they are read off the build's config and the address
                 bar and are true whatever the platform is doing.
   ========================================================================== */

// The Log out item. Rendered ONLY in Cognito mode (see UserBadge), which
// guarantees an <AuthProvider> above us — so calling the react-oidc hook here is
// safe. Local/IAP dev has no provider and never mounts this.
function LogoutItem({ cfg }: { cfg: CognitoConfig }) {
  const oidc = useOidcAuth();

  async function logout() {
    // Clear the local token + stop silent renew first, so nothing re-attaches a
    // stale Bearer while the redirect is in flight.
    try {
      await oidc.removeUser();
    } catch {
      // best-effort; proceed to the Cognito logout regardless.
    }
    const url = cognitoLogoutUrl(cfg, `${window.location.origin}/`);
    if (url) {
      // Clears the Cognito session, then bounces back to the app root where the
      // AuthGate restarts sign-in (a real logout, not just a local token wipe).
      window.location.assign(url);
    } else {
      // No hosted domain configured (older deploy): local-only sign-out.
      window.location.assign('/');
    }
  }

  return (
    <button
      type="button"
      role="menuitem"
      className={styles.menuItem}
      onClick={() => void logout()}
    >
      Log out
    </button>
  );
}

function Row({
  label,
  value,
  title,
  resolved = true,
}: {
  label: string;
  value: string;
  title?: string;
  /** False dims the value to the disabled step: this is a hole, not a reading. */
  resolved?: boolean;
}) {
  return (
    <div className={styles.row} title={title}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue} data-resolved={resolved}>
        {value}
      </span>
    </div>
  );
}

/* Display preference, not a destination. It used to be an icon-only button
   parked in the bottom corner of the nav rail with nothing else beside it —
   a control with no neighbours and no label, which is exactly the sort of
   orphan an operations rail should not carry. It belongs with the operator's
   other per-account facts. Two explicit radio items rather than one toggle:
   a toggle labelled with the state you would move to never says which state
   you are in. */
function ThemeChoice() {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  return (
    <div className={styles.themeRow} role="group" aria-label="Theme">
      <span className={styles.rowLabel}>Theme</span>
      <div className={styles.themeChoice}>
        {/* No moon and no sun. §8 tell #8: an icon has to encode a state or
            trigger an action, and a crescent beside the word `Dark` does
            neither — it is the glyph-beside-its-own-noun this bar refuses
            everywhere else. The words are the control. */}
        {(['dark', 'light'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="menuitemradio"
            aria-checked={theme === t}
            data-on={theme === t}
            className={styles.themeBtn}
            onClick={() => setTheme(t)}
          >
            {t === 'dark' ? 'Dark' : 'Light'}
          </button>
        ))}
      </div>
    </div>
  );
}

export function UserBadge() {
  const { data, error, isFetching, refetch, dataUpdatedAt, errorUpdatedAt } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const cfg = cognitoConfig();
  const user = data?.user ?? null;

  // Close on outside-click or Escape; Escape hands focus back to the trigger,
  // so a keyboard operator is never dropped at the top of the document.
  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // The first actionable item takes focus when the menu opens.
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  /* Which terminal event happened last. `isError` is not usable here: on a
     query with no data, query-core clears `error` and returns to `pending` at
     the top of every fetch, so the flag drops out mid-retry. Both stamps
     survive that. */
  const failed = errorUpdatedAt > 0 && errorUpdatedAt > dataUpdatedAt;

  /* `useAuth` is declared as a session-stable read — 24h stale time, no
     retry — which is right for an identity and wrong for an identity that
     never arrived: once the read fails, nothing ever asks again, and the badge
     would still say `—` an hour after the platform came back. So it re-asks
     itself while it is broken, and stops the moment it is not.

     30s, not the fleet's 5s: identity does not change under an operator, and
     the console has better things to spend a failing platform's sockets on.
     The root fix belongs in the hook (a `refetchInterval` gated on the error
     state) and is flagged in the shell's report. */
  useEffect(() => {
    if (!failed) return;
    const t = window.setInterval(() => void refetch(), 30_000);
    return () => window.clearInterval(t);
  }, [failed, refetch]);

  const lastReason = useRef<string | null>(null);
  useEffect(() => {
    if (error instanceof Error && error.message !== '') lastReason.current = error.message;
  }, [error]);

  /* `/api/auth/me` answers `{user: null}` for a request it cannot attribute —
     an IAP deployment with no forwarded identity — and throws only when the
     read itself fails. Two different facts, two different renderings. */
  const anonymous = data !== undefined && data.user === null;
  /* Latched, for the same reason the flags are: `error` is cleared at the top
     of every retry, and a fault line that renames its own cause halfway
     through a retry is a fault line nobody trusts. */
  const reason = failed ? (lastReason.current ?? 'read failed') : null;

  /* What the trigger says. Never nothing: an empty 176px slot at the bezel is
     the console declining to answer a question it was asked. */
  const address = user ? user.email : anonymous ? 'anonymous' : NO_VALUE;
  const short = user ? (user.email.split('@')[0] ?? user.email) : address;
  const label = user
    ? `Signed in as ${user.email}. Account menu.`
    : anonymous
      ? 'No principal — the API attributed this session to nobody. Account menu.'
      : `Principal unresolved${reason ? ` — ${reason}` : ''}. Account menu.`;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.badge}
        data-resolved={user !== null}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.email}>{address}</span>
        <span className={styles.emailShort}>{short}</span>
        <span className={styles.chevron} aria-hidden="true" data-open={open}>
          <Icon name="chevron-down" size={12} />
        </span>
      </button>

      {open && (
        <div className={styles.menu} role="menu" ref={menuRef}>
          <div className={styles.menuHeader}>
            <span className={styles.menuEmail} data-resolved={user !== null}>
              {address}
            </span>
          </div>

          {/* Provenance, not decoration: these decide whether the next command
              this operator issues will be authorised. The first three are the
              API's answer and go to `—` when it has none; the last two are the
              build's own config and the address bar, and stay true whatever
              the platform is doing. */}
          <div className={styles.rows}>
            <Row label="Tenant" value={user?.tenant ?? NO_VALUE} resolved={user !== null} />
            <Row label="Role" value={user?.role ?? NO_VALUE} resolved={user !== null} />
            <Row
              /* The auth PLANE, which is a build fact and stays true when the
                 principal cannot be resolved. `dev principal` is only claimed
                 where the API actually answered with one. This is the PLANE
                 the identity came from, not the transport plane the floor
                 names — two different questions that happen to share a
                 noun. */
              label="Auth"
              value={cfg ? 'cognito' : user ? 'dev principal' : 'none configured'}
              title={
                cfg
                  ? `OIDC against ${cfg.authority}`
                  : 'No VITE_COGNITO_* configured — the API serves its local development principal'
              }
            />
            {/* NO HOST ROW. The console's floor carries `127.0.0.1:5182`
                permanently, on every route, in the band that owns deployment
                identity — and it survives a dead API for the same reason this
                popover's version would, because it comes off the address bar.
                A second copy 900px above it is the duplication this round set
                out to remove. */}
            <ThemeChoice />
          </div>

          {/* The identity read's own fault line — the endpoint, the error
              verbatim, and the one verb that can fix it. Sits above the
              session verb, because there is no point ending a session the
              console cannot see. */}
          {reason !== null && (
            <div className={styles.fault} role="status">
              <span className={styles.faultText}>
                <Icon name="alert-triangle" size={11} />
                /api/auth/me — {reason}
              </span>
              <button
                type="button"
                role="menuitem"
                className={styles.faultVerb}
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                <Icon name="refresh" size={11} />
                {isFetching ? 'Retrying' : 'Retry'}
              </button>
            </div>
          )}

          {cfg ? (
            <LogoutItem cfg={cfg} />
          ) : (
            // Local/IAP dev: no OIDC session to end. One line where the verb
            // would be, rather than a disabled button that lies about being one.
            <span className={styles.menuNote}>No session to end in dev</span>
          )}
        </div>
      )}
    </div>
  );
}
